import React, { useEffect, useState, useId } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BedDouble, ChevronDown, ChevronLeft, ChevronRight, LayoutGrid, List, MapPin, Search, SlidersHorizontal, Wallet } from "lucide-react";
import { Drawer, DrawerTrigger, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerClose } from "../components/ui/drawer";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import CityCombobox from "../components/CityCombobox";
import BudgetSelector, { BUDGET_PRESETS } from "../components/BudgetSelector";
import RoomTypePills from "../components/RoomTypePills";
import { HotelCardSkeleton } from "../components/HotelCard";
import { trackSearch, trackZeroResults, sendBackendEvent } from "../analytics";

const AMENITIES = [["WiFi", "📶", "Free WiFi"], ["Parking", "🅿️", "Free Parking"], ["AC", "❄️", "Air Conditioning"], ["Restaurant", "🍽️", "Restaurant on-site"], ["Security", "🔒", "24hr Security"], ["Pool", "🏊", "Swimming Pool"]];
const SORTS = [["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["rating", "Highest rated"], ["newest", "Newest"]];
const RATINGS = [["", "Any"], ["3.0", "3.0+"], ["4.0", "4.0+"], ["4.5", "4.5+"]];
const FILTER_KEYS = ["min_price", "max_price", "room_type", "amenities", "rating", "sort"];

const shortTZS = value => (value >= 1000 ? `${Math.round(value / 1000)}k` : String(value));

// Budget/room values as the shared selector components expect them, derived from the URL.
const budgetValue = params => {
  const min = params.get("min_price"), max = params.get("max_price");
  return min || max ? { min: Number(min || 0), max: max ? Number(max) : null } : null;
};
const budgetParams = budget => ({
  min_price: budget?.min ? String(budget.min) : "",
  max_price: budget?.max != null ? String(budget.max) : "",
});
const budgetLabel = budget => {
  if (!budget) return "Any budget";
  const preset = BUDGET_PRESETS.find(p => p.min === (budget.min || 0) && p.max === budget.max);
  if (preset) return preset.label;
  return budget.max == null ? `${shortTZS(budget.min)}+` : `${shortTZS(budget.min || 0)} – ${shortTZS(budget.max)}`;
};
const roomValue = rooms => (rooms.length === 1 ? rooms[0] : "");
const roomLabel = rooms => (rooms.length === 0 ? "Any room" : rooms.length === 1 ? rooms[0] : `${rooms.length} room types`);

const pageList = (page, pages) => {
  const wanted = [...new Set([1, page - 1, page, page + 1, pages])].filter(p => p >= 1 && p <= pages).sort((a, b) => a - b);
  return wanted.flatMap((p, i) => (i && p - wanted[i - 1] > 1 ? [`gap-${p}`, p] : [p]));
};

const readView = () => { try { return localStorage.getItem("hs_results_view") === "list" ? "list" : "grid"; } catch { return "grid"; } };

function FilterSection({ label, children }) {
  return <fieldset className="space-y-3 border-b border-hs-border pb-6 last:border-0">
    <legend className="section-label mb-3">{label}</legend>
    {children}
  </fieldset>;
}

function FilterControls({ params, update, clear, active }) {
  const ratingGroup = useId();
  const rooms = params.getAll("room_type");
  const amenities = params.getAll("amenities");
  const toggle = (key, values, value) => update({ [key]: values.includes(value) ? values.filter(v => v !== value) : [...values, value] });
  return <div className="space-y-6">
    <FilterSection label="City">
      <CityCombobox value={params.get("city") || ""} onChange={city => update({ city })} placeholder="All cities" />
    </FilterSection>
    <FilterSection label="Budget per night">
      <BudgetSelector wrap value={budgetValue(params)} onChange={budget => update(budgetParams(budget))} />
    </FilterSection>
    <FilterSection label="Room type">
      <RoomTypePills wrap value={roomValue(rooms)} onChange={type => update({ room_type: type ? [type] : [] })} />
    </FilterSection>
    <FilterSection label="Amenities">
      <div className="space-y-1">
        {AMENITIES.map(([value, icon, label]) => (
          <label key={value} className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-1 text-sm text-hs-charcoal hover:bg-hs-sage">
            <input type="checkbox" checked={amenities.includes(value)} onChange={() => toggle("amenities", amenities, value)} className="h-4 w-4 rounded accent-hs-amber" />
            <span aria-hidden="true">{icon}</span>{label}
          </label>
        ))}
      </div>
    </FilterSection>
    <FilterSection label="Rating">
      <div className="flex flex-wrap gap-2">
        {RATINGS.map(([value, label]) => {
          const checked = value === (params.get("rating") || "");
          return <label key={value} className={`pill cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-hs-amber/40 ${checked ? "pill-active" : ""}`}>
            <input type="radio" name={ratingGroup} value={value} checked={checked} onChange={() => update({ rating: value })} className="sr-only" />{label}
          </label>;
        })}
      </div>
    </FilterSection>
    <FilterSection label="Sort by">
      <select aria-label="Sort by" className="input-base" value={params.get("sort") || "price_asc"} onChange={e => update({ sort: e.target.value })}>
        {SORTS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
    </FilterSection>
    {active && <button type="button" onClick={clear} className="min-h-[44px] text-sm font-semibold text-hs-amber hover:text-hs-amber-dk">Clear all filters</button>}
  </div>;
}

// Compact filter pill in the sticky bar that opens its control in a popover.
function BarPill({ icon: Icon, label, active, width = "w-80", children }) {
  const [open, setOpen] = useState(false);
  return <Popover open={open} onOpenChange={setOpen}>
    <PopoverTrigger asChild>
      <button type="button" className={`flex min-h-[40px] shrink-0 items-center gap-2 whitespace-nowrap rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${active ? "border-hs-amber bg-hs-amber-lt text-hs-amber" : "border-transparent bg-hs-sage text-hs-charcoal hover:border-hs-border"}`}>
        <Icon className="h-4 w-4" aria-hidden="true" />{label}<ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden="true" />
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" className={`${width} max-w-[calc(100vw-2rem)] rounded-xl border-hs-border p-4 font-sans`}>
      {children(() => setOpen(false))}
    </PopoverContent>
  </Popover>;
}

export default function DirectorySearch({ api, Navbar, Footer, HotelCard }) {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const city = params.get("city") || "";
  const page = Math.max(1, Number(params.get("page")) || 1);
  const [hotels, setHotels] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [view, setView] = useState(readView);

  const update = (changes, resetPage = true) => setParams(previous => {
    const next = new URLSearchParams(previous);
    // Drop stale booking-era URL parameters whenever search controls change.
    ["checkin", "checkout", "guests", "sort_by"].forEach(key => next.delete(key));
    if (resetPage) next.delete("page");
    Object.entries(changes).forEach(([key, value]) => {
      next.delete(key);
      (Array.isArray(value) ? value : [value]).forEach(item => { if (item !== "" && item != null) next.append(key, item); });
    });
    return next;
  }, {replace: true});
  const clear = () => update({min_price: "", max_price: "", room_type: [], amenities: [], rating: "", sort: ""});
  const active = FILTER_KEYS.some(key => params.has(key));
  const changeView = next => { setView(next); try { localStorage.setItem("hs_results_view", next); } catch {} };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      const source = new URLSearchParams(query);
      const request = new URLSearchParams();
      ["city", "search", "min_price", "max_price", "room_type", "amenities", "rating", "sort", "page", "limit"].forEach(key => source.getAll(key).forEach(value => request.append(key, value)));
      if (!request.has("sort")) request.set("sort", "price_asc");
      if (!request.has("page")) request.set("page", "1");
      if (!request.has("limit")) request.set("limit", "20");
      const low = request.get("min_price"), high = request.get("max_price");
      if (low && high && Number(low) > Number(high)) {
        setError("Minimum budget must not exceed maximum budget."); setLoading(false); return;
      }
      try {
        const res = await api.get(`/hotels?${request}`, {signal: controller.signal});
        if (!active) return;
        const count = Number(res.headers?.["x-total-count"] ?? res.data.length);
        const pageCount = Number(res.headers?.["x-total-pages"] ?? Math.ceil(count / Number(request.get("limit"))));
        if (Number(request.get("page")) > Math.max(1, pageCount)) {
          setParams(previous => { const next = new URLSearchParams(previous); next.set("page", String(Math.max(1, pageCount))); return next; }, {replace: true});
          return;
        }
        setHotels(res.data); setTotal(count); setPages(pageCount);
        const selectedCity = request.get("city") || "";
        trackSearch(selectedCity, low ? Number(low) : undefined, high ? Number(high) : undefined, count);
        const event = {city: selectedCity, budget_min: low ? Number(low) : undefined, budget_max: high ? Number(high) : undefined};
        void sendBackendEvent("hotel_search", {...event, results_count: count});
        if (!count) { trackZeroResults(selectedCity, event.budget_min, event.budget_max); void sendBackendEvent("zero_results", event); }
      } catch (err) {
        if (active) { setHotels([]); setTotal(0); setPages(0); setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not load hotels. Please try again."); }
      } finally { if (active) setLoading(false); }
    }, 300);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [api, query, retry, setParams]);

  const rooms = params.getAll("room_type");
  const budget = budgetValue(params);
  const hotelWord = total === 1 ? "hotel" : "hotels";
  const heading = loading ? "Finding hotels…" : error ? "Search could not be completed" : `${total} ${hotelWord} in ${city || "Tanzania"}`;
  const gridClass = view === "list" ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3";

  return <div className="min-h-screen bg-hs-white">
    <Helmet><title>{city ? `Hotels in ${city}` : "Hotels in Tanzania"} | Habari Stays</title><meta name="description" content={`${city ? `Hotels in ${city}, Tanzania.` : "Hotels across Tanzania."} Compare prices and amenities. Contact hotels directly.`} /><link rel="canonical" href={`https://habaristays.com/hotels${city ? `?city=${encodeURIComponent(city)}` : ""}`} /></Helmet>
    <Navbar />

    {/* Condensed search bar */}
    <div className="sticky top-16 z-30 border-b border-hs-border bg-white px-4 py-3">
      <div className="mx-auto flex max-w-7xl items-center gap-3">
        <div className="-my-1 flex min-w-0 flex-1 items-center gap-2 overflow-x-auto py-1 scrollbar-hide">
          <BarPill icon={MapPin} label={city || "All cities"} active={!!city}>
            {close => <><p className="section-label mb-2">Where are you going?</p><CityCombobox autoFocus value={city} onChange={next => { update({ city: next }); if (next) close(); }} placeholder="All cities" /></>}
          </BarPill>
          <BarPill icon={Wallet} label={budgetLabel(budget)} active={!!budget} width="w-[22rem]">
            {close => <><p className="section-label mb-3">Budget per night</p><BudgetSelector wrap value={budget} onChange={next => { update(budgetParams(next)); close(); }} /></>}
          </BarPill>
          <BarPill icon={BedDouble} label={roomLabel(rooms)} active={rooms.length > 0}>
            {close => <><p className="section-label mb-3">Room type</p><RoomTypePills wrap value={roomValue(rooms)} onChange={type => { update({ room_type: type ? [type] : [] }); close(); }} /></>}
          </BarPill>
        </div>
        <p role="status" className="hidden shrink-0 text-sm text-hs-muted lg:block">{loading ? "Updating hotels…" : error ? "Search could not be completed" : `${total} ${hotelWord} found`}</p>
        <div className="shrink-0 lg:hidden">
          <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
            <DrawerTrigger asChild>
              <button type="button" className={`flex min-h-[40px] items-center gap-2 rounded-xl border px-3 text-sm font-semibold ${active ? "border-hs-amber bg-hs-amber-lt text-hs-amber" : "border-hs-border bg-white text-hs-charcoal"}`}><SlidersHorizontal className="h-4 w-4" aria-hidden="true" />Filters</button>
            </DrawerTrigger>
            <DrawerContent className="max-h-[90dvh] bg-white font-sans">
              <DrawerHeader className="text-left"><DrawerTitle className="text-lg font-bold text-hs-charcoal">Filter hotels</DrawerTitle><DrawerDescription className="text-hs-muted">Compare nightly prices, rooms and amenities.</DrawerDescription></DrawerHeader>
              <div className="overflow-y-auto px-4 pb-4"><FilterControls params={params} update={update} clear={clear} active={active} /></div>
              <div className="border-t border-hs-border bg-white p-4 pb-safe">
                <DrawerClose asChild><button type="button" className="btn-primary w-full">Apply Filters{!loading && !error ? ` · ${total} ${hotelWord}` : ""}</button></DrawerClose>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </div>

    <div className="mx-auto flex max-w-7xl items-start gap-8 px-4 pb-16">
      <aside aria-label="Hotel filters" className="sticky top-32 hidden max-h-[calc(100dvh-8rem)] w-[260px] shrink-0 overflow-y-auto border-r border-hs-border bg-white px-5 py-6 lg:block">
        <FilterControls params={params} update={update} clear={clear} active={active} />
      </aside>

      <section className="min-w-0 flex-1 pt-6" aria-label="Search results" aria-busy={loading}>
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-lg font-semibold text-hs-charcoal">{heading}</h1>
          <div className="flex items-center gap-2">
            <label className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
              <span className="sr-only">Search hotel names</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-hs-muted" aria-hidden="true" />
              <input type="search" aria-label="Search hotel names and descriptions" value={params.get("search") || ""} onChange={e => update({search: e.target.value})} className="input-base min-h-[44px] py-2.5 pl-9" placeholder="Hotel name" />
            </label>
            <div className="flex shrink-0 rounded-xl border border-hs-border bg-white p-1" role="group" aria-label="Results layout">
              {[["grid", LayoutGrid, "Grid"], ["list", List, "List"]].map(([value, Icon, label]) => (
                <button key={value} type="button" aria-pressed={view === value} onClick={() => changeView(value)} className={`flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium ${view === value ? "bg-hs-amber-lt text-hs-amber" : "text-hs-slate hover:text-hs-charcoal"}`}>
                  <Icon className="h-4 w-4" aria-hidden="true" /><span className="hidden sm:inline">{label}</span><span className="sr-only sm:hidden">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {error ? (
          <div role="alert" className="card flex flex-col items-center gap-3 p-8 text-center">
            <p className="text-sm text-hs-slate">{error}</p>
            <button type="button" onClick={() => setRetry(value => value + 1)} className="btn-outline">Try again</button>
          </div>
        ) : loading ? (
          <div className={gridClass}>{Array.from({ length: 6 }, (_, i) => <HotelCardSkeleton key={i} layout={view} />)}</div>
        ) : !hotels.length ? (
          <div className="flex flex-col items-center px-4 py-16 text-center">
            <span className="mb-4 text-6xl" aria-hidden="true">🏨</span>
            <p className="mb-2 text-lg font-semibold text-hs-charcoal">No hotels found{city ? ` in ${city}` : ""}</p>
            <p className="mb-6 max-w-sm text-sm text-hs-muted">Try adjusting your filters or search a different city.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <button type="button" onClick={clear} className="btn-primary">Clear filters</button>
              <Link to="/#cities" className="btn-outline py-3">Browse all cities</Link>
            </div>
          </div>
        ) : (
          <div className={gridClass}>{hotels.map(hotel => <HotelCard key={hotel.id} hotel={hotel} layout={view} />)}</div>
        )}

        {!error && pages > 1 && (
          <nav aria-label="Results pages" className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <button type="button" disabled={loading || page <= 1} onClick={() => update({page: String(page - 1)}, false)} className="flex min-h-[44px] items-center gap-1 rounded-full border border-hs-border bg-white px-4 text-sm font-medium text-hs-slate hover:border-hs-amber hover:text-hs-amber disabled:pointer-events-none disabled:opacity-40"><ChevronLeft className="h-4 w-4" aria-hidden="true" />Previous</button>
            {pageList(page, pages).map(item => typeof item === "string"
              ? <span key={item} className="px-1 text-hs-muted" aria-hidden="true">…</span>
              : <button key={item} type="button" disabled={loading} aria-current={item === page ? "page" : undefined} aria-label={`Page ${item}`} onClick={() => update({page: String(item)}, false)} className={`flex h-11 min-w-[44px] items-center justify-center rounded-full px-3 text-sm font-semibold ${item === page ? "bg-hs-amber text-white" : "border border-hs-border bg-white text-hs-slate hover:border-hs-amber hover:text-hs-amber"}`}>{item}</button>)}
            <button type="button" disabled={loading || page >= pages} onClick={() => update({page: String(page + 1)}, false)} className="flex min-h-[44px] items-center gap-1 rounded-full border border-hs-border bg-white px-4 text-sm font-medium text-hs-slate hover:border-hs-amber hover:text-hs-amber disabled:pointer-events-none disabled:opacity-40">Next<ChevronRight className="h-4 w-4" aria-hidden="true" /></button>
          </nav>
        )}
      </section>
    </div>
    <Footer />
  </div>;
}
